import type { Joi } from '@ksyos/koi';
import { Map } from 'immutable';
import type { ISchemaContainer } from '../../common/validation/ISchemaContainer';

export type ValidationErrors = Map<string, string | undefined>;

export const emptyValidationErrors: ValidationErrors = Map();

export type IValidationResult<T> = IValidationValidResult<T> | IValidationInvalidResult;

interface IValidationValidResult<T> {
    errors: ValidationErrors;
    hasErrors: false;
    item: T;
}

interface IValidationInvalidResult {
    errors: ValidationErrors;
    hasErrors: true;
    item?: never;
}

const mapDetail = (map: { [key: string]: string }, detail: Joi.ValidationErrorItem) =>
    (map[detail.path.join('.')] = detail.type.replace('.', '_'));

export function validate<T>(schemaContainer: ISchemaContainer<T>, object: any): IValidationResult<T> {
    const result = schemaContainer.schema.validate(object, {
        abortEarly: false,
        stripUnknown: { arrays: false, objects: true },
    });
    if (result.error) {
        const map: { [key: string]: string } = {};
        for (const detail of result.error.details) {
            if (detail.type === 'alternatives.match' && detail.context && detail.context['details']) {
                const contextDetails = detail.context['details'];
                for (const contextDetail of contextDetails) {
                    mapDetail(map, contextDetail);
                }
            } else {
                mapDetail(map, detail);
            }
        }

        return { errors: Map(map), hasErrors: true };
    } else {
        return { errors: emptyValidationErrors, hasErrors: false, item: result.value };
    }
}

export function updateValidationErrorsForOne<T>(
    schemaContainer: ISchemaContainer<T>,
    object: any,
    errors: ValidationErrors,
    name: string
): ValidationErrors {
    const { errors: newErrors } = validate(schemaContainer, object);
    if (newErrors.has(name)) {
        return errors.set(name, newErrors.get(name));
    } else {
        return errors.remove(name);
    }
}
